import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BRAND_LOGO_ALT, ORGANIZATION_NAME, brandLogo } from '../branding';
import defprofile from '../Images/defprofile.png';
import shapeTop from '../Images/Shape 1.svg';
import shapeBottom from '../Images/Shape 2.svg';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { app } from '../firebase';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

function SchoolIdComp() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const userId = searchParams.get('userId') || '';
  const nameParam = searchParams.get('name');
  const courseParam = searchParams.get('course');
  const cohortParam = searchParams.get('cohort');
  const durationParam = searchParams.get('duration');

  const [eligibility, setEligibility] = useState({
    loading: true,
    allowed: false,
    status: '',
    reason: ''
  });
  const [studentRecord, setStudentRecord] = useState(null);
  const [showBackFirst, setShowBackFirst] = useState(false);
  const [photoSrc, setPhotoSrc] = useState(defprofile);
  const frontRef = useRef(null);
  const backRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const checkEligibility = async () => {
      if (!userId) {
        if (isMounted) {
          setEligibility({ loading: false, allowed: false, status: '', reason: 'missing_user' });
        }
        return;
      }

      try {
        const db = getFirestore(app);
        const q = query(collection(db, 'users'), where('userId', '==', userId));
        const snap = await getDocs(q);
        if (!isMounted) return;
        if (snap.empty) {
          setEligibility({ loading: false, allowed: false, status: '', reason: 'not_found' });
          return;
        }

        const userData = snap.docs[0].data();
        const status = String(userData.userStatus || '').trim();
        const statusLower = status.toLowerCase();
        const allowed = statusLower === 'student' || statusLower === 'completed';
        setStudentRecord(userData);
        setEligibility({
          loading: false,
          allowed,
          status,
          reason: allowed ? '' : 'not_admitted'
        });
      } catch (error) {
        console.error('Error checking school ID eligibility:', error);
        if (isMounted) {
          setEligibility({ loading: false, allowed: false, status: '', reason: 'error' });
        }
      }
    };

    checkEligibility();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  const handleBack = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  const fullName = nameParam || studentRecord?.bio || 'Student Name';
  const courseName = courseParam || studentRecord?.course || 'Program Name';
  const cohort = cohortParam || studentRecord?.cohort || '—';
  const duration = durationParam || '—';

  const idInfo = {
    name: fullName,
    course: courseName,
    cohort,
    duration,
    idNumber: studentRecord?.admissionNumber || userId || '—',
    photo: studentRecord?.profilePic || defprofile
  };

  useEffect(() => {
    let isMounted = true;

    const toDataUrl = async (url) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };

    const resolvePhoto = async () => {
      const rawPhoto = idInfo.photo || defprofile;

      if (!rawPhoto || rawPhoto === defprofile) {
        if (isMounted) setPhotoSrc(defprofile);
        return;
      }

      try {
        if (rawPhoto.startsWith('data:')) {
          if (isMounted) setPhotoSrc(rawPhoto);
          return;
        }

        if (rawPhoto.startsWith('http://') || rawPhoto.startsWith('https://')) {
          try {
            const dataUrl = await toDataUrl(rawPhoto);
            if (isMounted) setPhotoSrc(dataUrl);
          } catch (error) {
            console.warn('Using direct photo URL (data conversion failed):', error);
            if (isMounted) setPhotoSrc(rawPhoto);
          }
          return;
        }

        const storage = getStorage(app);
        const refPath = storageRef(storage, rawPhoto);
        const url = await getDownloadURL(refPath);
        try {
          const dataUrl = await toDataUrl(url);
          if (isMounted) setPhotoSrc(dataUrl);
        } catch (error) {
          console.warn('Using download URL (data conversion failed):', error);
          if (isMounted) setPhotoSrc(url);
        }
      } catch (error) {
        console.error('Error resolving profile image URL:', error);
        if (isMounted) setPhotoSrc(defprofile);
      }
    };

    resolvePhoto();
    return () => {
      isMounted = false;
    };
  }, [idInfo.photo]);

  const verifyId = idInfo.idNumber || userId || '';
  const verifyUrl = verifyId
    ? `https://verify.ubunifucollege.com/school-id/${encodeURIComponent(verifyId)}`
    : '';
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(verifyUrl)}`;

  const handleToggleOrder = () => {
    setShowBackFirst((prev) => !prev);
  };

  const handleDownloadId = async () => {
    if (!eligibility.allowed) return;
    if (!frontRef.current || !backRef.current) return;

    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    const fetchAsDataUrl = async (url) => {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      return await blobToDataUrl(blob);
    };

    const convertImagesToDataUrls = async (rootEl) => {
      const imgs = Array.from(rootEl.querySelectorAll('img'));
      const restoreList = [];
      for (const img of imgs) {
        const src = img.getAttribute('src') || '';
        if (!src || src.startsWith('data:')) continue;
        try {
          const dataUrl = await fetchAsDataUrl(src);
          restoreList.push([img, src]);
          img.setAttribute('src', dataUrl);
        } catch (error) {
          // Keep original src if conversion fails.
          console.warn('Could not convert image to data URL for export:', error);
        }
      }
      await Promise.all(
        imgs.map((img) => (
          img.complete ? Promise.resolve() : new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          })
        ))
      );
      return restoreList;
    };

    const restoreFront = await convertImagesToDataUrls(frontRef.current);
    const restoreBack = await convertImagesToDataUrls(backRef.current);

    const [frontCanvas, backCanvas] = await Promise.all([
      html2canvas(frontRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }),
      html2canvas(backRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' })
    ]);

    [...restoreFront, ...restoreBack].forEach(([img, src]) => {
      img.setAttribute('src', src);
    });

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [frontCanvas.width, frontCanvas.height]
    });

    pdf.addImage(frontCanvas.toDataURL('image/png', 1.0), 'PNG', 0, 0, frontCanvas.width, frontCanvas.height);
    pdf.addPage([backCanvas.width, backCanvas.height], 'portrait');
    pdf.addImage(backCanvas.toDataURL('image/png', 1.0), 'PNG', 0, 0, backCanvas.width, backCanvas.height);

    const safeName = idInfo.name.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    pdf.save(`School_ID_${safeName || 'Student'}.pdf`);
  };

  return (
    <div className="certificatePage">
      <div className="certificateToolbar">
        <button className="btn btn-outline-dark" onClick={handleBack}>Back</button>
      </div>

      {eligibility.loading ? (
        <div className="certificateCanvas">
          <div className="certificateScaleWrap">
            <div className="certificateBorder">
              <p style={{ textAlign: 'center' }}>Checking admission status...</p>
            </div>
          </div>
        </div>
      ) : eligibility.allowed ? (
        <div className="schoolIdSection">
          <div className="schoolIdHeader">
            <div>
              <h5 className="schoolIdTitle">School ID</h5>
              <p className="schoolIdSubtitle">Toggle the order: front & back or back & front.</p>
            </div>
            <div>
              <button className="btn btn-outline-dark schoolIdButton" onClick={handleToggleOrder}>
                {showBackFirst ? 'Show Front & Back' : 'Show Back & Front'}
              </button>
              <button className="btn btn-dark schoolIdButton ms-2" onClick={handleDownloadId}>
                Download ID
              </button>
            </div>
          </div>

          <div className="schoolIdGrid">
            {(showBackFirst ? ['back', 'front'] : ['front', 'back']).map((side) => (
              <div
                key={side}
                className={`schoolIdCard ${side === 'front' ? 'schoolIdFront' : 'schoolIdBack'}`}
                ref={side === 'front' ? frontRef : backRef}
              >
                <div className="schoolIdNotch" />
                <img src={shapeTop} alt="" className="schoolIdShape schoolIdShapeTop" />
                <img src={shapeBottom} alt="" className="schoolIdShape schoolIdShapeBottom" />
                {side === 'front' ? (
                  <>
                    <div className="schoolIdFrontInner">
                      <div className="schoolIdLogoRow">
                        <img src={brandLogo} alt={BRAND_LOGO_ALT} className="schoolIdLogoMark" />
                      </div>

                      <div className="schoolIdAvatarWrap">
                        <img
                          src={photoSrc}
                          alt={idInfo.name}
                          onError={() => setPhotoSrc(defprofile)}
                        />
                      </div>

                      <p className="schoolIdName">{idInfo.name}</p>
                      <p className="schoolIdRole">STUDENT</p>
                      <p className="schoolIdIdLine">Student ID No: {idInfo.idNumber}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="schoolIdBackInner">
                      <img src={qrSrc} alt="Student QR code" className="schoolIdQr" />
                      <ul className="schoolIdTerms">
                        <li>This card is the property of {ORGANIZATION_NAME} and must be returned upon withdrawal or graduation.</li>
                        <li>It must be carried at all times while on campus.</li>
                        <li>Lost cards must be reported to the Registrar immediately.</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="certificateCanvas">
          <div className="certificateScaleWrap">
            <div className="certificateBorder">
              <h4 style={{ textAlign: 'center' }}>School ID Unavailable</h4>
              <p style={{ textAlign: 'center' }}>
                School ID is available only for admitted students.
              </p>
              {eligibility.status && (
                <p style={{ textAlign: 'center' }}>Current status: {eligibility.status}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SchoolIdComp;
